/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export class ScaleLinear {
  private _domain: [number, number];
  private _range: [number, number];
  // y = mx + c; store m and c param
  private m: number;
  private c: number;

  constructor(domain: [number, number], range: [number, number]) {
    this._domain = domain;
    this._range = range;
    this.m = (this._range[1] - this._range[0]) / (this._domain[1] - this._domain[0]);
    this.c = this._range[0] - this.m * this._domain[0];
  }

  transform(value: number) {
    return this.m * value + this.c;
  }

  invert(value: number) {
    return (value - this.c) / this.m;
  }

  domain() {
    return this._domain;
  }

  range() {
    return this._range;
  }
}

export class ScaleLog {
  private _domain: [number, number];
  private _linearDomain: [number, number];
  private _range: [number, number];
  private linearScale: ScaleLinear;

  constructor(domain: [number, number], range: [number, number]) {
    this._linearDomain = [Math.log(domain[0]), Math.log(domain[1])];
    this._domain = domain;
    this._range = range;
    this.linearScale = new ScaleLinear(this._linearDomain, this._range);
  }

  transform(value: number) {
    const v = Math.log(value);
    return this.linearScale.transform(v);
  }

  invert(value: number) {
    const v = this.linearScale.invert(value);
    return Math.pow(10, v);
  }

  domain() {
    return this._domain;
  }

  range() {
    return this._range;
  }
}
